import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper
} from '@mui/material';
import {
  LocationOn,
  GpsFixed,
  Warning,
  Info
} from '@mui/icons-material';
import { 
  isGeolocationSupported, 
  getBrowserInstructions, 
  getBrowserName 
} from './utils';
import QRCodeDisplay from './QRCodeDisplay';
import type { GeolocationError } from './types';

interface GeolocationDialogProps {
  open: boolean;
  isLoading: boolean;
  error: GeolocationError | null;
  accuracy?: number;
  username: string;
  onRequestLocation: () => void;
  onQRSuccess: () => void;
}

/**
 * Модальное окно запроса геопозиции
 * Блокирующий диалог - нельзя закрыть без разрешения
 * Показывает QR-код если точность ПК низкая (> 50м)
 */
export const GeolocationDialog: React.FC<GeolocationDialogProps> = ({
  open,
  isLoading,
  error,
  accuracy,
  username,
  onRequestLocation,
  onQRSuccess
}) => {
  // Проверяем поддержку Geolocation API
  const isSupported = isGeolocationSupported();
  
  // Состояние для показа QR-кода
  const [showQR, setShowQR] = useState(false);
  const [qrError, setQRError] = useState<string>('');
  
  // Определяем, нужно ли показывать QR-код
  // Если точность > 50 метров - переключаемся на QR
  const needsQR = accuracy !== undefined && accuracy > 50;

  // Автоматически переключаемся на QR при получении координат с низкой точностью
  useEffect(() => {
    if (needsQR && !isLoading) {
      console.log(`📱 Точность ${accuracy}м > 50м. Показываем QR-код`);
      setShowQR(true);
    }
  }, [needsQR, isLoading, accuracy]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={true}
      onClose={(event, reason) => {
        // Блокируем закрытие по клику на backdrop
        if (reason === 'backdropClick') {
          return false;
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 2,
        pt: 3
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 1
        }}>
          <LocationOn sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h5" component="div" fontWeight="bold">
            Требуется доступ к местоположению
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Показываем QR-код если точность низкая */}
        {(needsQR || showQR) && !isLoading && (
          <QRCodeDisplay
            username={username}
            onSuccess={onQRSuccess}
            onError={(err) => setQRError(err)}
          />
        )}

        {/* Обычный режим - если QR не показываем */}
        {!needsQR && !showQR && (
          <>
            {/* Если браузер не поддерживает Geolocation API */}
            {!isSupported && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Браузер не поддерживает геолокацию</AlertTitle>
                Ваш браузер ({getBrowserName()}) не поддерживает Geolocation API.
                <br />
                Пожалуйста, используйте современный браузер: Chrome, Firefox, Edge или Safari.
              </Alert>
            )}

            {/* Основная информация */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: 'info.light',
                color: 'info.contrastText'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Info sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    Для продолжения работы необходимо определить ваше местоположение
                  </Typography>
                  <Typography variant="body2">
                    Это нужно для учета вашего текущего местонахождения в системе.
                    Ваши координаты будут сохранены и обновляться при каждом входе.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Ошибка */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Ошибка получения местоположения</AlertTitle>
                {error.message}
                
                {/* Инструкция при отказе */}
                {error.code === 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Как разрешить доступ:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      bgcolor: 'rgba(255,255,255,0.9)', 
                      p: 1, 
                      borderRadius: 1,
                      color: 'text.primary'
                    }}>
                      {getBrowserInstructions()}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {/* Индикатор загрузки */}
            {isLoading && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                gap: 2,
                py: 3
              }}>
                <CircularProgress size={48} />
                <Typography variant="body1" color="text.secondary" fontWeight="bold">
                  Определяем ваше местоположение...
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Система делает несколько попыток для максимальной точности
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    ⏱️ Это может занять 15-30 секунд для достижения высокой точности
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Дополнительная информация */}
            {!error && !isLoading && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100',
                  borderLeft: 4,
                  borderColor: 'warning.main'
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Warning sx={{ color: 'warning.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>
                      ⚡ Как получить МАКСИМАЛЬНУЮ точность (до 5-10 метров):
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Включите GPS</strong> на вашем устройстве (не только WiFi)
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Выйдите на улицу</strong> - GPS работает плохо в помещении
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Подождите 10-20 секунд</strong> для стабилизации GPS сигнала
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Находитесь на открытом пространстве</strong> (не под крышей/деревьями)
                        </Typography>
                      </li>
                    </Box>
                    <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                      <Typography variant="caption">
                        💡 Система сделает несколько попыток и выберет самый точный результат
                      </Typography>
                    </Alert>
                  </Box>
                </Box>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        {/* Показываем кнопку только если не QR режим */}
        {!needsQR && !showQR && (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={onRequestLocation}
            disabled={!isSupported || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <GpsFixed />}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 'bold',
              textTransform: 'none'
            }}
          >
            {isLoading ? 'Получение координат...' : 'Разрешить доступ к местоположению'}
          </Button>
        )}
      </DialogActions>

    </Dialog>
  );
};

export default GeolocationDialog;
